import { Component, ViewChildren, QueryList} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, Validators, ValidatorFn, FormArray } from '@angular/forms';
import { EncrypterService } from 'src/app/shared/services/encrypter.service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Subscription } from 'rxjs';
import { SessionService } from 'src/app/shared/services/session.service';
import { customJwtDecode as jwtDecode } from 'src/app/shared/helpers/custom-jwt';
import { DomSanitizer } from '@angular/platform-browser';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { MyRequestsService } from '../my-requests.service';
declare var $: any;

@Component({
    selector: 'app-public-form',
    templateUrl: './public-form.component.html',
    styleUrls: ['./public-form.component.css'],
    standalone: false
})
export class PublicFormComponent {
  previewForm: FormGroup;
  formStructure: any[] = [];
  isLoading: boolean = true;
  formId!: number;
  title: string = '';
  showSidebar = true;
  formBuilderList: any;
  decoded_token: any;
  formTitle: any;
  fileMap: Record<string, File> = {}; // store uploaded files by fieldId
  private readonly MAX_FILE_MB = 5;
  private readonly ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
  private readonly ALLOWED_PDF_TYPES = ['application/pdf'];

  encryptedURLToken: string;
  private valueChangeSubscriptions: Subscription[] = []; // To store subscriptions

  @ViewChildren('dateInput') dateInputs!: QueryList<any>;

  constructor(
    private _sessionsService: SessionService,
    private route: ActivatedRoute,
    private router: Router,
    private formApiService: MyRequestsService,
    private encrypter: EncrypterService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer
  ) {
    this.previewForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.encryptedURLToken = this.route.snapshot.paramMap.get('token');
    //console.log('Id: ', this.encryptedURLToken)
    if (this.encryptedURLToken) {
      try {
        let decryptedURLToken = this.encrypter.aesDecrypt(this.encryptedURLToken);
        decryptedURLToken = JSON.parse(decryptedURLToken)
        this.formId = decryptedURLToken.formId;
        console.log('formId: ', this.formId)
        this.loadAndBuildForm(this.formId);
      } catch (error) {
        this.isLoading = false;
        console.error("Failed to decrypt form ID.", error);
        this.toastr.error("Invalid form link.");
      }
    } else {
      this.isLoading = false;
      this.toastr.error("No form ID provided.");
    }

    let session_obj = JSON.parse(this._sessionsService.get_user_session());
    let token = (session_obj).token;
    this.decoded_token = jwtDecode(token);
    console.log('Token: ',this.decoded_token)

    this.getFormBuilderList();

  }

  ngOnDestroy(): void {
    this.valueChangeSubscriptions.forEach(sub => sub.unsubscribe());
  }

  loadAndBuildForm(componentId: number): void {
    this.isLoading = true;
    this.formApiService.getFormFields({ component: componentId })
      .pipe(debounceTime(100))
      .subscribe({
        next: (resData: any) => {
          if (resData && resData.statusCode) {
            try {
              const decryptedData = this.encrypter.aesDecrypt(resData.commonData);
              const parsedJson = JSON.parse(decryptedData);
              const fieldList = parsedJson || [];
              // console.log('FieldList-->', fieldList)

              if (!Array.isArray(fieldList)) {
                throw new Error("Parsed data is not an array.");
              }

              fieldList.sort((a: any, b: any) => a.display_order - b.display_order);

              this.formStructure = fieldList.map(field => {
                const fieldTypeName = field.field_type_name?.toLowerCase() || '';

                const isContentField = ['declaration', 'release', 'paragraph', 'heading'].includes(fieldTypeName);

                const safeContent = isContentField
                  ? this.sanitizer.bypassSecurityTrustHtml(field.field_values || '')
                  : null;

                const options = !isContentField && field.field_values
                  ? field.field_values.split('|#*|').map(opt => opt.trim())
                  : [];

                
                // Parse field_attribute for child fields or time validations or table validation
                let childFields = [];
                let timeValidation = null;
                let tableColumns = null;

                if (field.field_attribute) {
                  try {
                    const parsedAttribute = JSON.parse(String(field.field_attribute));
                    if (fieldTypeName === 'time' && parsedAttribute.timeValidation) {
                      timeValidation = parsedAttribute.timeValidation;
                    }
                    else if (fieldTypeName === 'table' && parsedAttribute.tableValidation) {
                      tableColumns = parsedAttribute.tableValidation.columns;
                    }
                    else {
                      childFields = parsedAttribute;
                    }
                  } catch (e) {
                    console.error("Failed to parse field_attribute for field", field.field_id, e);
                  }
                }

                    const newField: any = {
                  ...field,
                  safeContent,
                  options,
                  child_fields_config: childFields, // Store parsed child field configuration
                  timeValidationConfig: timeValidation, // Store parsed time validation configuration
                  tableColumns: (field as any).tableValidation?.columns || [] // Store parsed table columns
                };

                // Pre-calculate leaf columns for table fields to ensure stability and proper header generation
                if (fieldTypeName === 'table') {
                  newField.precalculatedLeafColumns = this.getLeafColumns(newField.child_fields_config || []);
                  // If child_fields_config is empty, try tableColumns
                  if (newField.precalculatedLeafColumns.length === 0) {
                    newField.precalculatedLeafColumns = this.getLeafColumns(newField.tableColumns || []);
                  }
                }

                return newField;

              });

              this.buildReactiveForm(this.formStructure);

            } catch (e) {
              console.error("CRITICAL: Failed to process form data.", e);
              this.formStructure = [];
            }
          } else {
            this.toastr.error(resData.message || "Could not load form data.");
            this.formStructure = [];
          }
          this.isLoading = false;
           if (typeof $ !== 'undefined') {
            setTimeout(() => {
              this.initializeDatePickers();
            }, 500); // Wait for DOM to render
          }
        },
        error: () => {
          this.toastr.error("An API error occurred while loading the form.");
          this.isLoading = false;
        }
      });
  }

  buildReactiveForm(fields: any[]): void {
    // Clear old controls and subscriptions
    Object.keys(this.previewForm.controls).forEach(key => {
      this.previewForm.removeControl(key);
    });
    this.valueChangeSubscriptions.forEach(sub => sub.unsubscribe());
    this.valueChangeSubscriptions = [];

    fields.forEach(field => {
      const validators = [];
      const fieldTypeName = field.field_type_name?.toLowerCase();
      const inputType = Number(field.input_type); // This comes from the API payload (1:numeric, 2:alphanumeric, 3:alpha)
      const allowSpecial = field.special_character_status?.toLowerCase() === 'yes';

      // Required
      if (field.mandatory === 1) {
        validators.push(fieldTypeName === 'declaration' ? Validators.requiredTrue : Validators.required);
      }

      let pattern: RegExp | null = null;

      const isTextInputField = ['text', 'long text', 'mail to', 'phone number', 'age', 'integer'].includes(fieldTypeName);

      if (isTextInputField) {
        if (fieldTypeName === 'phone number') {
          validators.push(this.phoneNumberValidator.bind(this));
        } else if (fieldTypeName !== 'mail to') {
          switch (inputType) {
            case 1: // Numeric
              pattern = allowSpecial ? /^[0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/ : /^[0-9\s]*$/;
              break;
            case 2: // Alphanumeric
              pattern = allowSpecial ? /^[a-zA-Z0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/ : /^[a-zA-Z0-9\s]*$/;
              break;
            case 3: // Alpha only
              pattern = allowSpecial ? /^[a-zA-Z\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/ : /^[a-zA-Z\s]*$/;
              break;
            default:
              pattern = null;
          }
        }
      }

      // Time validation
      if (fieldTypeName === 'time') {
        validators.push(this.timeValidator(field.timeValidationConfig));
      }


      // Email validation only for "mail to"
      if (fieldTypeName === 'mail to') {
        validators.push(Validators.email);
      }

      // Length
      if (field.min_length > 0) validators.push(Validators.minLength(field.min_length));
      if (field.max_length > 0) validators.push(Validators.maxLength(field.max_length));

      // Date validation
      if (['date', 'dob', 'future date'].includes(fieldTypeName)) {
        validators.push((control: AbstractControl) => {
          if (!control.value) return null;

          let date: Date | null = null;

          if (control.value instanceof Date) {
            date = control.value;
          } else if (typeof control.value === 'string') {
            const regex = /^\d{4}[-/]\d{2}[-/]\d{2}$/; // YYYY-MM-DD or YYYY/MM/DD
            if (!regex.test(control.value)) {
              return { invalidDate: true };
            }
            date = new Date(control.value.replace(/\//g, "-"));
          }

          if (!date || isNaN(date.getTime())) {
            return { invalidDate: true };
          }

          const today = new Date();
          today.setHours(0, 0, 0, 0); // Normalize today to start of day
          date.setHours(0, 0, 0, 0); // Normalize selected date to start of day


          if (fieldTypeName === 'dob') {
            // DOB must be in the past, not today or future
            return date < today ? null : { dobInvalid: true };
          }
          if (fieldTypeName === 'future date') {
            // Future date must be strictly in the future (tomorrow or later)
            return date > today ? null : { futureDate: true };
          }
          return null; // For generic 'date' type, just valid format
        });
      }

        if (fieldTypeName === 'table') {
        const tableArray = this.fb.array<FormGroup>([]);
        const leafCols = field.precalculatedLeafColumns || [];

        // Check if there are saved values for this table
        if (field.field_values) {
          try {
            const savedRows = JSON.parse(field.field_values);
            if (Array.isArray(savedRows)) {
              savedRows.forEach((rowVal: any) => {
                const rowGroup = this.createTableRow(leafCols);

                // MIGRATION: Map old keys to new keys if necessary
                const patchedValue: any = {};
                leafCols.forEach((col: any) => {
                  const newKey = col.header;
                  // If we have the exact key, use it
                  if (rowVal.hasOwnProperty(newKey)) {
                    patchedValue[newKey] = rowVal[newKey];
                  } else {
                    // Fallback: try to find by just the last part of the header (for backward compatibility)
                    // e.g. "Year - From" might have been saved as "From"
                    const originalHeader = col.originalHeader;
                    if (originalHeader && rowVal.hasOwnProperty(originalHeader)) {
                      patchedValue[newKey] = rowVal[originalHeader];
                    } else {
                      patchedValue[newKey] = '';
                    }
                  }
                });

                rowGroup.patchValue(patchedValue);
                tableArray.push(rowGroup);
              });
            }
          } catch (e) {
            // If parsing fails or no values, add one empty row
            tableArray.push(this.createTableRow(leafCols));
          }
        } else {
          // Initialize with one empty row by default
          tableArray.push(this.createTableRow(leafCols));
        }

        this.previewForm.addControl(field.field_id.toString(), tableArray);
      } else {
        this.previewForm.addControl(
          field.field_id.toString(),
          this.fb.control(field.default_value || '', validators)
        );
      }

      // Add listener for parent fields to control child field visibility 
      if (field.child_fields_config && field.child_fields_config.length > 0) {
        const control = this.previewForm.get(field.field_id.toString());
        if (control) {
          this.valueChangeSubscriptions.push(
            control.valueChanges.subscribe(() => {
              this.updateChildFieldVisibility(field.field_id);
            })
          );
        }
      }
    });

    // Initial check for child field visibility
    this.formStructure.forEach(field => {
      if (field.child_fields_config && field.child_fields_config.length > 0) {
        this.updateChildFieldVisibility(field.field_id);
      }
    });

  }

  // Custom validator for time fields
  timeValidator(config: any): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }

      let inputTime24: string | null = null;
      const value: string = control.value;

      // Regex for HH:MM (24-hour)
      const time24Regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
      // Regex for HH:MM AM/PM (12-hour)
      const time12Regex = /^(0?\d|1[0-2]):([0-5]\d)\s*(AM|PM)$/i;

      if (time24Regex.test(value)) {
        inputTime24 = value;
      } else if (time12Regex.test(value)) {
        // Convert 12-hour to 24-hour
        const parts = value.match(time12Regex);
        if (parts) {
          let hours = parseInt(parts[1], 10);
          const minutes = parts[2];
          const ampm = parts[3].toUpperCase();

          if (ampm === 'PM' && hours < 12) {
            hours += 12;
          } else if (ampm === 'AM' && hours === 12) {
            hours = 0; // 12 AM is 00 in 24-hour format
          }
          inputTime24 = `${String(hours).padStart(2, '0')}:${minutes}`;
        }
      }

      if (!inputTime24) {
        return { timeInvalid: true }; // Invalid format
      }

      // Now use inputTime24 for min/max comparisons
      const minTime = config?.minTime;
      const maxTime = config?.maxTime;

      if (minTime && inputTime24 < minTime) {
        return { minTimeInvalid: true };
      }
      if (maxTime && inputTime24 > maxTime) {
        return { maxTimeInvalid: true };
      }

      return null;
    };
  }


  updateChildFieldVisibility(parentFieldId: number): void {
    this.formStructure.forEach(field => {
      // Find the parent field in formStructure
      const parentConfig = this.formStructure.find(f => f.field_id === parentFieldId);

      // Check if this field is a child of the parentFieldId and if parentConfig.child_fields_config is an array
      if (parentConfig && Array.isArray(parentConfig.child_fields_config)) {
        parentConfig.child_fields_config.forEach((childRule: any) => {
          if (childRule.childId === field.field_id) {
            const parentControl = this.previewForm.get(parentFieldId.toString());
            const childControl = this.previewForm.get(field.field_id.toString());

            if (parentControl && childControl) {
              const parentValue = parentControl.value;
              const expectedValue = childRule.expectedValue;

              // Handle "null" expectedValue from JSON as actual null
              const isExpectedValueNull = expectedValue === null || expectedValue === 'null';

              let shouldShow = false;

              if (parentConfig.field_type_name.toLowerCase() === 'checkbox') {
                // If parent is a checkbox, parentValue will be an array.
                // Child should show if expectedValue is present in parentValue array.
                shouldShow = Array.isArray(parentValue) && parentValue.includes(expectedValue);
              } else if (parentConfig.field_type_name.toLowerCase() === 'radio' || parentConfig.field_type_name.toLowerCase() === 'list') {
                // For radio/list, compare directly
                shouldShow = (isExpectedValueNull && (parentValue === null || parentValue === undefined || parentValue === '')) ||
                  (!isExpectedValueNull && parentValue === expectedValue);
              }


              if (shouldShow) {
                if (childControl.disabled) {
                  childControl.enable();
                  // Re-apply ALL original validators for the field
                  const originalField = this.formStructure.find(f => f.field_id === field.field_id);
                  if (originalField) {
                    const validators = [];
                    const fieldTypeName = originalField.field_type_name?.toLowerCase();
                    const inputType = Number(originalField.input_type);
                    const allowSpecial = originalField.special_character_status?.toLowerCase() === 'yes';

                    // Re-add Required
                    if (originalField.mandatory === 1) {
                      validators.push(fieldTypeName === 'declaration' ? Validators.requiredTrue : Validators.required);
                    }

                    let pattern: RegExp | null = null;
                    const isTextInputField = ['text', 'long text', 'mail to', 'phone number', 'integer', 'age'].includes(fieldTypeName);

                    if (isTextInputField) {
                      if (fieldTypeName === 'phone number') {
                        validators.push(this.phoneNumberValidator.bind(this));
                      }
                      else if (fieldTypeName !== 'mail to') {
                        switch (inputType) {
                          case 1: // Numeric
                            pattern = allowSpecial
                              ? /^[0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/
                              : /^[0-9\s]*$/;
                            break;
                          case 2: // Alphanumeric
                            pattern = allowSpecial
                              ? /^[a-zA-Z0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/
                              : /^[a-zA-Z0-9\s]*$/;
                            break;
                          case 3: // Alpha only
                            pattern = allowSpecial
                              ? /^[a-zA-Z\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/
                              : /^[a-zA-Z\s]*$/;
                            break;
                        }
                      }
                      if (pattern) validators.push(Validators.pattern(pattern));
                    }

                    // Re-add Email validation
                    if (fieldTypeName === 'mail to') {
                      validators.push(Validators.email);
                    }

                    // Re-add Time validation
                    if (fieldTypeName === 'time') {
                      validators.push(this.timeValidator(originalField.timeValidationConfig));
                    }

                    // Re-add Length
                    if (originalField.min_length > 0) validators.push(Validators.minLength(originalField.min_length));
                    if (originalField.max_length > 0) validators.push(Validators.maxLength(originalField.max_length));

                    // Re-add Date validation
                    if (['date', 'dob', 'future date'].includes(fieldTypeName)) {
                      validators.push((control: AbstractControl) => {
                        if (!control.value) return null;

                        let date: Date | null = null;

                        if (control.value instanceof Date) {
                          date = control.value;
                        } else if (typeof control.value === 'string') {
                          const regex = /^\d{4}[-/]\d{2}[-/]\d{2}$/;
                          if (!regex.test(control.value)) {
                            return { invalidDate: true };
                          }
                          date = new Date(control.value.replace(/\//g, "-"));
                        }

                        if (!date || isNaN(date.getTime())) {
                          return { invalidDate: true };
                        }

                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        date.setHours(0, 0, 0, 0);

                        if (fieldTypeName === 'dob') {
                          return date < today ? null : { dobInvalid: true };
                        }
                        if (fieldTypeName === 'future date') {
                          return date > today ? null : { futureDate: true };
                        }
                        return null;
                      });
                    }

                    childControl.setValidators(validators);
                  }
                  childControl.updateValueAndValidity();
                }
              } else {
                if (childControl.enabled) {
                  childControl.disable();
                  childControl.reset();
                  childControl.clearValidators();
                  childControl.updateValueAndValidity();
                }
              }
            }
          }
        });
      }
    });

  }

  getGridClass(field: any): string {
    const colSpan = (field.column_num || 4) >= 4 ? 12 : 6;
    return `col-sm-${colSpan} mrg-bot-15`;
  }

  onFileChange(event: Event, fieldId: number | string): void {
    const input = event.target as HTMLInputElement;
    const control = this.previewForm.get(String(fieldId)); // Get the form control

    if (!input.files || input.files.length === 0) {
      delete this.fileMap[String(fieldId)];
      control?.setValue(null); // Clear the control value
      control?.markAsTouched();
      control?.updateValueAndValidity(); // Update validity
      return;
    }

    const file = input.files[0];
    const field = this.formStructure.find(f => f.field_id.toString() === String(fieldId));
    const fieldTypeName = field?.field_type_name.toLowerCase();

    let expectedTypes: string[] = [];
    if (fieldTypeName === 'image file') {
      expectedTypes = this.ALLOWED_IMAGE_TYPES;
    } else if (fieldTypeName === 'pdf file') {
      expectedTypes = this.ALLOWED_PDF_TYPES;
    } else {
      this.toastr.error('Unsupported file field type.');
      input.value = ''; // Clear the file input
      control?.setValue(null);
      control?.markAsTouched();
      control?.updateValueAndValidity();
      return;
    }

    if (!expectedTypes.includes(file.type)) {
      this.toastr.error(`Only ${fieldTypeName === 'image file' ? 'image' : 'PDF'} files are allowed for this field.`);
      input.value = '';
      control?.setValue(null);
      control?.markAsTouched();
      control?.updateValueAndValidity();
      return;
    }

    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > this.MAX_FILE_MB) {
      this.toastr.error(`File too large. Max ${this.MAX_FILE_MB} MB.`);
      input.value = '';
      control?.setValue(null);
      control?.markAsTouched();
      control?.updateValueAndValidity();
      return;
    }

    // Store the actual File object in the map
    this.fileMap[String(fieldId)] = file;

    // Set a non-null value to the form control to indicate a file is selected for validation purposes.
    // The actual submission value will come from fileMap.
    control?.setValue(file.name); // Set the name for display/validation
    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity(); // Recalculate validity with the new value

  }

  onSubmit(): void {
    this.previewForm.markAllAsTouched();

    // Custom check for overall form validity for visible controls
    const allControlsValid = this.formStructure.every(field => {
      const control = this.previewForm.get(field.field_id.toString());
      // A field is valid if it's not visible, or it's visible AND valid.
      return !this.isFieldVisible(field) || (control && control.valid && control.enabled);
    });

    // Also explicitly check if mandatory file fields have a file
    const mandatoryFileFieldsValid = this.formStructure.every(field => {
      const fieldTypeName = field.field_type_name?.toLowerCase();
      if (this.isFieldVisible(field) && field.mandatory === 1 && (fieldTypeName === 'image file' || fieldTypeName === 'pdf file')) {
        return !!this.fileMap[field.field_id.toString()]; // Must have an entry in fileMap
      }
      return true;
    });


    if (!allControlsValid || !mandatoryFileFieldsValid) {
      this.toastr.error('Please fill out all required fields correctly.');
      return;
    }

    const fieldValues: Record<string, any> = {};
    const filePromises: Array<Promise<any>> = [];

    // 1. Process all file uploads first
    Object.keys(this.fileMap).forEach(key => {
      const field = this.formStructure.find(f => f.field_id.toString() === key);
      const control = this.previewForm.get(key);
      if (field && this.isFieldVisible(field) && control && control.enabled) { // Ensure it's a visible and enabled file field
        const file = this.fileMap[key];
        filePromises.push(this.readFileAsBase64(file, key));
      }
    });

    // 2. Wait for all files to be processed, then gather other fields
    Promise.all(filePromises)
      .then(fileResults => {
        fileResults.forEach(({ fieldId, payload }) => {
          const field = this.formStructure.find(f => f.field_id.toString() === fieldId);
          if (field) {
            fieldValues[fieldId] = {
              label: field.field_name,
              value: payload   // ✅ wrap uploaded file info inside value
            };
          }
        });


        // 3. Gather non-file values for *visible and enabled* fields (after files are handled)
        this.formStructure.forEach(field => {
          const fieldIdStr = field.field_id.toString();
          const control = this.previewForm.get(fieldIdStr);
          const fieldTypeName = field.field_type_name?.toLowerCase();

          // Only process if it's visible, enabled, AND NOT a file field (already processed above)
          if (
            this.isFieldVisible(field) &&
            control &&
            control.enabled &&
            !(fieldTypeName === 'image file' || fieldTypeName === 'pdf file')
          ) {
            let valueToSubmit = control.value;

            // Handle date objects
            if (['date', 'dob', 'future date'].includes(fieldTypeName) && valueToSubmit instanceof Date) {
              valueToSubmit = valueToSubmit.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            }
            // For checkbox, ensure value is a joined string
            else if (fieldTypeName === 'checkbox' && Array.isArray(valueToSubmit)) {
              valueToSubmit = valueToSubmit.join('|#*|');
            }
             // For table, stringify the array of objects
            else if (fieldTypeName === 'table') {
              valueToSubmit = JSON.stringify(valueToSubmit);
            }

            fieldValues[fieldIdStr] = {
              label: field.field_name,
              value: valueToSubmit
            };
          }
        });

        // 4. Final payload
        const formPayload = {
          component_id: this.formId,
          user_id: this.decoded_token.id,
          fields: fieldValues,
          created_by: Number(this.decoded_token.id),
          user_type: 'Employee',
          customeraccountid: this.decoded_token.tp_account_id,
          emp_code: Number(this.decoded_token.emp_code),
          masterId: null
        };

        // 5. API call
        this.formApiService.savePreview(formPayload).subscribe({
          next: (res: any) => {
            if (res.statusCode) {
              this.toastr.success('Application submitted successfully!');
              this.previewForm.reset();
              this.fileMap = {}; // Clear file map after successful submission
              // Re-evaluate visibility after reset
              this.formStructure.forEach(field => {
                if (field.child_fields_config && Array.isArray(field.child_fields_config) && field.child_fields_config.length > 0) {
                  this.updateChildFieldVisibility(field.field_id);
                }
              });
            } else {
              this.toastr.error(res.message || 'Failed to save application.');
            }
          },
          error: (err) => {
            console.error('API Error:', err);
            this.toastr.error('API call failed. Please try again.');
          }
        });
      })
      .catch(error => {
        console.error('Error processing files or submitting form:', error);
        this.toastr.error('Error processing files. Please try again.');
      });
  }

  // Helper to convert File to Base64
  private readFileAsBase64(file: File, fieldId: string): Promise<{ fieldId: string, payload: any }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        // strip data url prefix: "data:<mime>;base64,<data>"
        const commaIdx = result.indexOf(',');
        const base64 = commaIdx > -1 ? result.substring(commaIdx + 1) : result;

        resolve({
          fieldId,
          payload: {
            name: file.name,
            mime: file.type,
            size: file.size,
            base64
          }
        });
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });

  }

  toggle(): void {
    this.showSidebar = !this.showSidebar;
  }

  getFormBuilderList(key: string = '') {
    if (key && key.length < 3) {
      return;
    }
    this.formApiService.getFormLists({
      customerAccountId: this.decoded_token.tp_account_id.toString(),
      id: '',
      actionType: 'All',
    })
      .pipe(debounceTime(100))
      .subscribe({
        next: (resData: any) => {
          if (resData.statusCode) {
            this.formBuilderList = JSON.parse(
              this.encrypter.aesDecrypt(resData.commonData)
            );
            // console.log('formBuilderList: ', this.formBuilderList);
            const formData = this.formBuilderList || [];
            // console.log('formData: ', formData)
            if (!Array.isArray(formData)) {
              console.error("Form data is not an array", formData);
              this.formTitle = "Form Not Found";
              return;
            }

            const selectedForm = formData.find((item: any) => item.id === this.formId);
            // console.log('Form details: ', selectedForm)

            if (selectedForm) {
              this.formTitle = selectedForm.scheme_component_name;
            } else {
              this.formTitle = 'Form Not Found';
            }
          } else {
            this.formBuilderList = [];
            this.formTitle = 'Form Not Found';
          }
        },
        error: (error) => {
          console.error('Error fetching Form Builder:', error);
          this.formBuilderList = [];
          this.formTitle = 'Form Not Found';
        }
      });
  }

  toggleRadioSelection(fieldId: string | number, option: string): void {
    const control = this.previewForm.get(String(fieldId));
    if (control) {
      // If the same option is clicked again, unselect it (toggle behavior)
      if (control.value === option) {
        control.setValue(null);
      } else {
        control.setValue(option);
      }
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  // Handle checkbox selection (ADD this method)
  onCheckboxToggle(fieldId: string | number, option: string, checked: boolean): void {
    const control = this.previewForm.get(String(fieldId));
    if (!control) return;

    // Ensure control.value is an array, or initialize it as such
    let current: string[] = Array.isArray(control.value) ? [...control.value] : (control.value ? [control.value] : []);

    const idx = current.indexOf(option);

    if (checked && idx === -1) {
      current.push(option);
    } else if (!checked && idx > -1) {
      current.splice(idx, 1);
    }

    control.setValue(current);
    control.markAsTouched();
    control.markAsDirty();

  }

  goToFormBuilder() {
    this.router.navigate(['form/formbuilder', this.encryptedURLToken]);
  }

  getDateConfig(fieldType: string) {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today to start of day

    switch (fieldType.toLowerCase()) {
      case 'dob': // Date of Birth must be in the past
        return {
          isAnimated: true,
          containerClass: 'theme-default',
          dateInputFormat: 'YYYY-MM-DD',
          maxDate: new Date(today.setDate(today.getDate() - 1)) // Max date is yesterday
        };

      case 'future date': // Must be in the future
        return {
          isAnimated: true,
          containerClass: 'theme-default',
          dateInputFormat: 'YYYY-MM-DD',
          minDate: new Date(today.setDate(today.getDate() + 1)) // Min date is tomorrow
        };

      default: // Generic date
        return {
          isAnimated: true,
          containerClass: 'theme-default',
          dateInputFormat: 'YYYY-MM-DD'
        };
    }

  }

  isFieldVisible(field: any): boolean {
    // Find if this field is a child in any parent’s config
    const parent = this.formStructure.find(f =>
      Array.isArray(f.child_fields_config) && f.child_fields_config.some((rule: any) => rule.childId === field.field_id)
    );

    if (!parent) return true; // not a child → always visible

    const parentControl = this.previewForm.get(parent.field_id.toString());
    // Ensure child_fields_config is an array before filtering
    const rules = Array.isArray(parent.child_fields_config) ? parent.child_fields_config.filter((r: any) => r.childId === field.field_id) : [];

    for (let rule of rules) {
      const parentValue = parentControl?.value;
      const expectedValue = rule.expectedValue;

      // Handle "null" expectedValue from JSON as actual null or empty string/undefined
      const isExpectedValueNullOrEmpty = (expectedValue === null || expectedValue === 'null' || expectedValue === '');

      if (parent.field_type_name.toLowerCase() === 'checkbox') {
        // For checkboxes, parentValue is an array. Check if the expected value is in it.
        if (Array.isArray(parentValue) && parentValue.includes(expectedValue)) {
          return true;
        }
      } else {
        // For radio/list, direct comparison
        if (isExpectedValueNullOrEmpty) {
          if (parentValue === null || parentValue === undefined || parentValue === '') {
            return true;
          }
        } else {
          if (parentValue === expectedValue) {
            return true;
          }
        }
      }
    }
    return false; // otherwise hidden

  }

  // Parses the field_attribute for time validation configuration
  parseFieldAttribute(attribute: string): any {
    try {
      return JSON.parse(attribute);
    } catch (e) {
      return null;
    }
  }

  phoneNumberValidator(control: AbstractControl) {
    const value = control.value;
    if (!value) return null; // allow empty if not required

    const pattern = /^[6-9]\d{9}$/; // starts with 6/7/8/9 and exactly 10 digits
    return pattern.test(value) ? null : { invalidPhoneNumber: true };
  }

   // --- Table Field Helpers ---

  // Flatten nested columns to get all leaf columns (actual inputs)
  
  getLeafColumns(columns: any[], prefix: string = ''): any[] {
    let leafColumns: any[] = [];
    columns.forEach(col => {
      const currentHeader = prefix ? `${prefix} - ${col.header}` : col.header;

      if (col.subColumns && col.subColumns.length > 0) {
        leafColumns = [...leafColumns, ...this.getLeafColumns(col.subColumns, currentHeader)];
      } else {
        // Create a shallow copy with the new composite header, but preserve original for reference
        leafColumns.push({
          ...col,
          header: currentHeader,
          originalHeader: col.header // Keep original header for potential fallback
        });
      }
    });
    return leafColumns;
  }

  // Create a FormGroup for a single table row
    createTableRow(leafColumns: any[]): FormGroup {
    const rowGroup = this.fb.group({});

    leafColumns.forEach(col => {
      // Use column Header as key to ensure readable JSON in preview
      const key = col.header;
      // Default value empty
      rowGroup.addControl(key, this.fb.control(''));
    });
    return rowGroup;
  }

  // Initialize table with existing data or clear it
  // (Not used directly in buildReactiveForm because we initialize empty there, but good for loading)

  // Add a new row to the table
  addTableRow(fieldId: any, leafColumns: any[]): void {
    const control = this.previewForm.get(String(fieldId)) as FormArray;
    if (control) {
      control.push(this.createTableRow(leafColumns));
      if (typeof $ !== 'undefined') {
        setTimeout(() => {
          this.initializeDatePickers();
        }, 100);
      }
    }
  }

  ngAfterViewInit() {
    if (typeof $ !== 'undefined') {
      setTimeout(() => {
        this.initializeDatePickers();
      }, 500);
    }
  }

  initializeDatePickers() {
    if (typeof $ !== 'undefined' && $.fn.datepicker) {
      $('.custom-datepicker').datepicker({
        dateFormat: 'dd/mm/yy',
        changeMonth: true,
        changeYear: true,
        onSelect: (dateText: any, inst: any) => {
          // We need to trigger the input event manually so Angular's form binding picks it up
          // However, simple trigger might not update the specific FormControl if it relies on (input) listener.
          // It usually works if DefaultValueAccessor is used.
          inst.input.trigger('input');
          inst.input[0].dispatchEvent(new Event('input', { bubbles: true }));
          inst.input[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  // Remove a row from the table
  removeTableRow(fieldId: any, index: number): void {
    const control = this.previewForm.get(String(fieldId)) as FormArray;
    if (control) {
      control.removeAt(index);
    }
  }

  // Get FormArray controls for looping in template
  getTableRowControls(fieldId: any): AbstractControl[] {
    const control = this.previewForm.get(String(fieldId)) as FormArray;
    return control ? control.controls : [];
  }

}